// MissingDataModal.jsx - Modal para usuarios con datos faltantes
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { FaExclamationTriangle, FaCheck, FaSpinner } from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL;

// Mapeo de campos a etiquetas legibles
const FIELD_LABELS = {
  wake_up_time: 'Hora de despertar',
  sleep_time: 'Hora de dormir',
  work_type: 'Tipo de trabajo',
  food_allergies: 'Alergias alimentarias',
  disliked_foods: 'Alimentos que no te gustan',
  pathologies: 'Condiciones de salud',
  eating_disorder_option: 'Seguimiento alimentario',
  training_days_per_week: 'Días de entrenamiento por semana',
  training_time: 'Horario de entrenamiento',
  training_location: 'Lugar de entrenamiento',
  training_experience: 'Experiencia de entrenamiento',
  available_equipment: 'Equipamiento disponible',
  physical_limitations: 'Limitaciones físicas',
  training_preferences: 'Preferencias de entrenamiento',
  diet_type: 'Tipo de dieta'
};

const WORK_TYPES = [
  { value: 'sedentario', label: 'Sedentario (oficina, escritorio)' },
  { value: 'ligero', label: 'Ligero (de pie, caminando poco)' },
  { value: 'moderado', label: 'Moderado (caminando bastante)' },
  { value: 'activo', label: 'Activo (trabajo físico)' },
  { value: 'muy_activo', label: 'Muy activo (trabajo muy físico)' }
];

const TRAINING_LOCATIONS = [
  { value: 'gimnasio', label: 'Gimnasio' },
  { value: 'casa', label: 'Casa' },
  { value: 'ambos', label: 'Ambos' },
  { value: 'aire_libre', label: 'Aire libre' }
];

const TRAINING_EXPERIENCE = [
  { value: 'principiante', label: 'Principiante (0-1 año)' },
  { value: 'intermedio', label: 'Intermedio (1-3 años)' },
  { value: 'avanzado', label: 'Avanzado (3+ años)' }
];

const EATING_DISORDER_OPTIONS = [
  { value: 'ninguno', label: 'Ninguno' },
  { value: 'videollamada', label: 'Prefiero hablar por videollamada' },
  { value: 'mensaje', label: 'Prefiero comunicarme por mensaje' }
];

const DIET_TYPES = [
  { value: 'Equilibrada', label: 'Equilibrada' },
  { value: 'Alta en Proteína', label: 'Alta en Proteína' },
  { value: 'Baja en Carbohidratos', label: 'Baja en Carbohidratos' },
  { value: 'Alta en Carbohidratos', label: 'Alta en Carbohidratos' },
  { value: 'Keto', label: 'Keto' },
  { value: 'Vegetariana', label: 'Vegetariana' },
  { value: 'Vegana', label: 'Vegana' },
  { value: 'Sin Gluten', label: 'Sin Gluten' },
  { value: 'Sin Lácteos', label: 'Sin Lácteos' },
];

const PATHOLOGIES = [
  'Ninguna', 'Diabetes', 'Hipertensión', 'Hipotiroidismo', 'Hipertiroidismo',
  'Síndrome de ovario poliquístico (SOP)', 'Resistencia a la insulina',
  'Colesterol alto', 'Triglicéridos altos', 'Problemas digestivos',
  'Intolerancia a la lactosa', 'Celiaquía', 'Otra'
];

const MissingDataModal = ({ userEmail, onComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [userName, setUserName] = useState('');
  const [formData, setFormData] = useState({
    wake_up_time: '',
    sleep_time: '',
    work_type: '',
    food_allergies: '',
    disliked_foods: '',
    pathologies: [],
    eating_disorder_option: '',
    training_days_per_week: '',
    training_time: '',
    training_location: '',
    training_experience: '',
    available_equipment: '',
    physical_limitations: '',
    training_preferences: '',
    diet_type: ''
  });

  useEffect(() => {
    if (userEmail) {
      checkMissingData();
    }
  }, [userEmail]);

  const checkMissingData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/user/check-missing-data/${userEmail}`);
      
      if (response.data.has_missing_data) {
        setMissingFields(response.data.missing_fields);
        setUserName(response.data.user_name);
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Error checking missing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      
      // Preparar datos para enviar (solo los campos faltantes)
      const dataToSend = {};
      missingFields.forEach(field => {
        const value = formData[field];
        if (value !== '' && value !== null && value !== undefined) {
          // Convertir strings separados por coma a arrays para ciertos campos
          if (['food_allergies', 'disliked_foods', 'available_equipment'].includes(field)) {
            dataToSend[field] = typeof value === 'string' 
              ? value.split(',').map(s => s.trim()).filter(s => s)
              : value;
          } else {
            dataToSend[field] = value;
          }
        }
      });
      
      await axios.post(`${API}/api/user/update-missing-data/${userEmail}`, dataToSend);
      
      toast.success('¡Datos actualizados correctamente!');
      setIsOpen(false);
      if (onComplete) onComplete();
    } catch (error) {
      console.error('Error saving missing data:', error);
      toast.error('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  const handlePathologyChange = (pathology) => {
    const current = formData.pathologies || [];
    if (pathology === 'Ninguna') {
      setFormData({ ...formData, pathologies: ['Ninguna'] });
    } else {
      const filtered = current.filter(p => p !== 'Ninguna');
      if (filtered.includes(pathology)) {
        setFormData({ ...formData, pathologies: filtered.filter(p => p !== pathology) });
      } else {
        setFormData({ ...formData, pathologies: [...filtered, pathology] });
      }
    }
  };

  const renderField = (field) => {
    const label = FIELD_LABELS[field] || field;
    
    switch (field) {
      case 'wake_up_time':
      case 'sleep_time':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Input
              type="time"
              value={formData[field]}
              onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
              className="bg-white/10 border-white/20 text-white"
            />
          </div>
        );
      
      case 'work_type':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Select value={formData[field]} onValueChange={(v) => setFormData({ ...formData, [field]: v })}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Selecciona..." />
              </SelectTrigger>
              <SelectContent>
                {WORK_TYPES.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      
      case 'training_location':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Select value={formData[field]} onValueChange={(v) => setFormData({ ...formData, [field]: v })}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Selecciona..." />
              </SelectTrigger>
              <SelectContent>
                {TRAINING_LOCATIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      
      case 'training_experience':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Select value={formData[field]} onValueChange={(v) => setFormData({ ...formData, [field]: v })}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Selecciona..." />
              </SelectTrigger>
              <SelectContent>
                {TRAINING_EXPERIENCE.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      
      case 'eating_disorder_option':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Select value={formData[field]} onValueChange={(v) => setFormData({ ...formData, [field]: v })}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Selecciona..." />
              </SelectTrigger>
              <SelectContent>
                {EATING_DISORDER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      
      case 'diet_type':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Select value={formData[field]} onValueChange={(v) => setFormData({ ...formData, [field]: v })}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Selecciona..." />
              </SelectTrigger>
              <SelectContent>
                {DIET_TYPES.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      
      case 'training_days_per_week':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Select value={formData[field]} onValueChange={(v) => setFormData({ ...formData, [field]: v })}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Selecciona..." />
              </SelectTrigger>
              <SelectContent>
                {[1,2,3,4,5,6,7].map(n => (
                  <SelectItem key={n} value={String(n)}>{n} días</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      
      case 'training_time':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Select value={formData[field]} onValueChange={(v) => setFormData({ ...formData, [field]: v })}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Selecciona..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="05:00-08:00">Mañana temprano (5-8 AM)</SelectItem>
                <SelectItem value="08:00-12:00">Mañana (8-12 PM)</SelectItem>
                <SelectItem value="12:00-15:00">Mediodía (12-3 PM)</SelectItem>
                <SelectItem value="15:00-18:00">Tarde (3-6 PM)</SelectItem>
                <SelectItem value="18:00-21:00">Noche (6-9 PM)</SelectItem>
                <SelectItem value="21:00-00:00">Noche tarde (9 PM+)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      
      case 'pathologies':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
              {PATHOLOGIES.map(pathology => (
                <Badge
                  key={pathology}
                  variant="outline"
                  className={`cursor-pointer transition-all ${
                    (formData.pathologies || []).includes(pathology)
                      ? 'bg-pink-500/30 border-pink-500 text-pink-300'
                      : 'border-white/20 text-gray-400 hover:border-pink-500/50'
                  }`}
                  onClick={() => handlePathologyChange(pathology)}
                >
                  {pathology}
                </Badge>
              ))}
            </div>
          </div>
        );
      
      case 'food_allergies':
      case 'disliked_foods':
      case 'available_equipment':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Textarea
              value={formData[field]}
              onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
              placeholder="Escribe separando por comas. Ej: maní, mariscos, lácteos"
              className="bg-white/10 border-white/20 text-white"
              rows={2}
            />
          </div>
        );
      
      case 'physical_limitations':
      case 'training_preferences':
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Textarea
              value={formData[field]}
              onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
              placeholder={field === 'physical_limitations' ? 'Describe cualquier lesión o limitación...' : 'Describe tus preferencias de entrenamiento...'}
              className="bg-white/10 border-white/20 text-white"
              rows={2}
            />
          </div>
        );
      
      default:
        return (
          <div key={field} className="space-y-2">
            <Label className="text-gray-300">{label}</Label>
            <Input
              value={formData[field]}
              onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
              className="bg-white/10 border-white/20 text-white"
            />
          </div>
        );
    }
  };

  if (loading || !isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(var(--brand-primary-glow),0.3)' }}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-yellow-500/20">
              <FaExclamationTriangle className="text-yellow-400 text-xl" />
            </div>
            <div>
              <DialogTitle className="text-white text-xl">
                ¡Hola {userName}! Necesitamos actualizar tus datos
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Algunos datos importantes no están completos. Por favor ayúdanos a completarlos para personalizar mejor tu plan.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-3 rounded-lg bg-pink-500/10 border border-pink-500/30">
            <p className="text-pink-300 text-sm">
              Solo te pedimos la información que falta. Esto nos ayuda a crear planes más personalizados para ti.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {missingFields.map(field => renderField(field))}
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            onClick={handleSubmit} 
            disabled={saving}
            className="bg-pink-600 hover:bg-pink-700 w-full"
          >
            {saving ? (
              <>
                <FaSpinner className="animate-spin mr-2" /> Guardando...
              </>
            ) : (
              <>
                <FaCheck className="mr-2" /> Guardar mis datos
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MissingDataModal;
